import { action, ClassModel, extend, register, types } from "@gadgetinc/mobx-quick-tree";
import { ConfigurationVariableValueContainer } from "./ConfigurationVariableValueContainer";
import type { Dominion } from "./database/Dominion";
import { EnvironmentType } from "./Environment";
import { ConfigurationVariableProblems } from "./problems/finders/ConfigurationVariableProblems";
import type { ProblemsList } from "./problems/ProblemsList";
import { contentHash, GadgetDomainModelClass, getAppSettings, getGadgetEnv } from "./utils";
import { encryptApplicationSecret } from "./appSecrets";
import { GadgetCurrentAppDominionId } from "./database/DominionLiterals";
import { AppSettings } from "./AppSettings";

const BaseConfigurationVariable = (name: string) => {
  class BaseConfigurationVariable extends GadgetDomainModelClass(name, {
    apiIdentifier: types.string,
    dominionID: types.optional(types.string, GadgetCurrentAppDominionId),
  }) {
    get dominion(): Dominion {
      return getGadgetEnv(this).extensionRegistry.dominion(this.dominionID);
    }
    @action
    resetDominionID() {
      this.dominionID = GadgetCurrentAppDominionId;
    }
    @action
    setApiIdentifier(value: string) {
      this.apiIdentifier = value;
    }
    @action
    setDominionID(dominionID: string) {
      this.dominionID = dominionID;
    }
  }

  return BaseConfigurationVariable;
};

/**
 * `ConfigurationVariable` represents one piece of configuration for an app that does have multi environments enabled. The config is handed to the app as an environment variable. The data can be encrypted at rest if it it's sensitive, and it can be different between the app's environments.
 */
@register
export class ConfigurationVariable extends extend(BaseConfigurationVariable("EnvironmentConfigurationVariable"), {
  value: types.maybeNull(types.string),
  isSecret: types.optional(types.boolean, false),
}) {
  @action
  makeSecret() {
    if (!this.isSecret && this) {
      this.isSecret = true;

      if (this.value) {
        const publicKey = getAppSettings(this).secretsPublicKey;
        this.value = encryptApplicationSecret(this.value, publicKey);
      }
    }
  }
  @action
  makePublic() {
    this.isSecret = false;
    this.value = "";
  }
  @action
  setValue(incomingValue: string) {
    if (this.isSecret && incomingValue) {
      const publicKey = getAppSettings(this).secretsPublicKey;
      this.value = encryptApplicationSecret(incomingValue, publicKey);
    } else {
      this.value = incomingValue;
    }
  }

  get problems(): ProblemsList {
    return ConfigurationVariableProblems(this);
  }

  get contentHash() {
    return contentHash([this.apiIdentifier, this.value]);
  }

  getValue() {
    return this.value;
  }

  get allProblems() {
    return this.problems.allProblemsWithReferenceNode(this);
  }
}

export const DefaultConfigurationVariableValues = { Development: {}, Production: {} } as const;

@register
export class LegacyConfigurationVariableEnvValues extends ClassModel({
  Development: types.optional(ConfigurationVariableValueContainer, {}),
  Production: types.optional(ConfigurationVariableValueContainer, {}),
}) {}

/**
 * `LegacyConfigurationVariable` represents one piece of configuration for an app that does not have multi environments enabled. The config is handed to the app as an environment variable. The data can be encrypted at rest if it it's sensitive, and it can be different between the app's environments.
 */
@register
export class LegacyConfigurationVariable extends extend(BaseConfigurationVariable("ConfigurationVariable"), {
  values: types.optional(
    types.late(() => LegacyConfigurationVariableEnvValues),
    DefaultConfigurationVariableValues
  ),
}) {
  get currentEnvValueContainer(): ConfigurationVariableValueContainer {
    const environmentType = getAppSettings(this).environmentType;
    return this.valueContainerForEnv(environmentType);
  }

  valueContainerForEnv(environmentType: EnvironmentType): ConfigurationVariableValueContainer {
    return environmentType == EnvironmentType.Production ? this.values.Production : this.values.Development;
  }

  @action
  makeSecret() {
    this.currentEnvValueContainer.makeSecret();
  }
  @action
  makePublic() {
    this.currentEnvValueContainer.makePublic();
  }
  @action
  setValue(incomingValue: string) {
    this.currentEnvValueContainer.setValue(incomingValue);
  }
  @action
  setValueForEnv(environmentType: EnvironmentType, incomingValue: string) {
    this.valueContainerForEnv(environmentType).setValue(incomingValue);
  }

  get problems(): ProblemsList {
    return ConfigurationVariableProblems(this);
  }

  get contentHash() {
    return contentHash([this.apiIdentifier, this.values.Development.contentHash, this.values.Production.contentHash]);
  }

  get value() {
    return this.currentEnvValueContainer.value;
  }

  get isSecret() {
    return this.currentEnvValueContainer.isSecret;
  }

  getValue() {
    return this.currentEnvValueContainer.value;
  }

  get allProblems() {
    return this.problems.allProblemsWithReferenceNode(this);
  }
}

export const AllConfigurationVariableUnion = types.union(ConfigurationVariable, LegacyConfigurationVariable);
export type AllConfigurationVariables = ConfigurationVariable | LegacyConfigurationVariable;
export const getConfigurationVariableTypeForSettings = (settings: AppSettings) =>
  settings.multiEnvironmentEnabled ? "EnvironmentConfigurationVariable" : "ConfigurationVariable";
